import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { useCallback, useRef, useState, type MouseEvent } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import MessagingLabel from 'LuxKit/Label/MessagingLabel'
import NeutralLabel from 'LuxKit/Label/NeutralLabel'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import Caption from 'LuxKit/Typography/Caption'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import ResponsiveDropdownList from 'tripPlanner/components/Common/Dropdowns/ResponsiveDropdownList'
import type { TripListRow } from './tripListTypes'

interface Props {
  isBooked: boolean
  row?: TripListRow
  disabled?: boolean
  onBookingChange?: (row: TripListRow, isBooked: boolean) => void
}

const BookingStatusAnchor = styled.span`
  display: inline-flex;
`

function TripListBookedLabel({ isBooked, row, disabled, onBookingChange }: Props) {
  const intl = useIntl()
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLSpanElement>(null)
  const editable = Boolean(row && !row.isLuxuryEscapesBooking && onBookingChange)
  const close = useCallback(() => setOpen(false), [])
  const stopPropagation = useCallback((event: MouseEvent) => event.stopPropagation(), [])
  const toggle = useCallback((event: MouseEvent) => {
    event.stopPropagation()
    if (!disabled) setOpen(value => !value)
  }, [disabled])
  const changeBookingStatus = useCallback((event: MouseEvent) => {
    event.stopPropagation()
    close()
    if (row && !disabled) onBookingChange?.(row, !isBooked)
  }, [close, disabled, isBooked, onBookingChange, row])
  const endIcon = editable ? <LineAngleDownIcon size="XS" aria-hidden /> : undefined
  const statusLabel = isBooked ? (
    <MessagingLabel variant="success" endIcon={endIcon}>
      <FormattedMessage id="dsjKlUcoW3tb6U9N7vAr" defaultMessage="Booked" description="Status label for trip planner list rows that are already booked" />
    </MessagingLabel>
  ) : (
    <NeutralLabel endIcon={endIcon}>
      <FormattedMessage id="hBcf69utwxOmb39CbOqE" defaultMessage="Not booked" description="Status label for trip planner list rows that have not yet been booked" />
    </NeutralLabel>
  )
  if (!editable) return statusLabel
  const label = intl.formatMessage({ defaultMessage: 'Change booking status for {title}', id: 'dPMSf26uDTilfro8ftQ7', description: 'Accessible label for changing a manually marked booking in the itinerary list' }, { title: row?.title })
  return (
    <>
      <BookingStatusAnchor ref={triggerRef} data-testid="trip-list-booking-anchor">
        <TextLink
          size="caption"
          weight="regular"
          underline={false}
          onClick={toggle}
          onMouseDown={stopPropagation}
          disabled={disabled}
          aria-label={label}
          aria-expanded={open}
        >
          {statusLabel}
        </TextLink>
      </BookingStatusAnchor>
      <ResponsiveDropdownList size="S" open={open && !disabled} onClose={close} triggerRef={triggerRef} anchorRef={triggerRef} placement="bottom-start" enableFlip portaled>
        <Sheet padding={12}>
          <Group direction="vertical" gap={8}>
            <TextButton kind="secondary" size="small" fit="full-width" onClick={changeBookingStatus} onMouseDown={stopPropagation} disabled={disabled}>
              {isBooked ? (
                <FormattedMessage defaultMessage="Mark as not booked" id="2ZSo6yrs7mpsYLfbTC7D" description="Action to clear a manual booked marker from an itinerary item without cancelling it" />
              ) : (
                <FormattedMessage defaultMessage="Mark as booked" id="aeZccwAmkKks-_2gV-CU" description="Action to record that the traveller has already booked an itinerary item" />
              )}
            </TextButton>
            {isBooked && <Caption variant="medium">
              <FormattedMessage defaultMessage="This won't cancel a reservation" id="JJDPfLjpArkrOGXx0mk2" description="Reassurance that clearing a manual booked marker does not cancel a reservation" />
            </Caption>}
          </Group>
        </Sheet>
      </ResponsiveDropdownList>
    </>
  )
}

export default TripListBookedLabel
